/**
 * <s-count-up> counts the first number inside it up from zero when it scrolls into view.
 * The final value is server-rendered, so it stays correct without JavaScript or with reduced motion.
 */
class SCountUp extends HTMLElement {
  connectedCallback() {
    if (this._started) return;
    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;

    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node && !/\d/.test(node.nodeValue)) node = walker.nextNode();
    if (!node) return;

    const match = node.nodeValue.match(/^(\D*)([\d,]+)(.*)$/s);
    if (!match) return;

    const [, prefix, digits, suffix] = match;
    const target = parseInt(digits.replace(/,/g, ''), 10);
    if (!Number.isFinite(target) || target === 0) return;

    this._node = node;
    this._parts = { prefix, suffix, target };
    node.nodeValue = `${prefix}0${suffix}`;

    this._observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this._observer.disconnect();
        this._run();
      }
    }, { threshold: 0.4 });
    this._observer.observe(this);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    cancelAnimationFrame(this._frame);
    if (this._node && this._parts) {
      const { prefix, suffix, target } = this._parts;
      this._node.nodeValue = `${prefix}${target.toLocaleString()}${suffix}`;
    }
  }

  _run() {
    this._started = true;
    const { prefix, suffix, target } = this._parts;
    const duration = 1400;
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this._node.nodeValue = `${prefix}${Math.round(target * eased).toLocaleString()}${suffix}`;
      if (progress < 1) this._frame = requestAnimationFrame(step);
    };

    this._frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('s-count-up')) customElements.define('s-count-up', SCountUp);
